import { Controller } from "@hotwired/stimulus";

// Wizard for the "+ New interaction" modal.
// 3 steps: pick mode (past / upcoming) → date+type → notes.
// Mode shapes the default occurred_at and the step-3 copy.
export default class extends Controller {
  static targets = [
    "step",
    "modeInput",
    "dateInput",
    "typeInput",
    "notesInput",
    "modeSummary",
    "dateSummary",
    "typeSummary",
    "stepLabel",
    "step3Title",
    "step3Help",
    "backButton",
    "nextButton",
    "submitButton",
  ];

  static values = {
    step: { type: Number, default: 1 },
    mode: { type: String, default: "" }, // "past" or "upcoming"
  };

  connect() {
    this.render();
  }

  pickPast(event) {
    event?.preventDefault();
    this.modeValue = "past";
    this.setDefaultDate("past");
    this.stepValue = 2;
    this.render();
  }

  pickUpcoming(event) {
    event?.preventDefault();
    this.modeValue = "upcoming";
    this.setDefaultDate("upcoming");
    this.stepValue = 2;
    this.render();
  }

  next(event) {
    event?.preventDefault();
    if (this.stepValue === 2) {
      // Validate step 2 before advancing
      if (!this.hasTypeInputTarget || !this.typeInputTarget.value) {
        this.flash(this.typeInputTarget, "Pick a type to continue.");
        return;
      }
      if (!this.dateInputTarget.value) {
        this.flash(this.dateInputTarget, "Pick a date and time.");
        return;
      }
    }
    this.stepValue += 1;
    this.render();
  }

  back(event) {
    event?.preventDefault();
    if (this.stepValue > 1) {
      this.stepValue -= 1;
      this.render();
    }
  }

  setDefaultDate(mode) {
    if (!this.hasDateInputTarget) return;
    const now = new Date();
    if (mode === "upcoming") {
      now.setDate(now.getDate() + 7);
      now.setMinutes(0, 0, 0);
    }
    // Format as datetime-local YYYY-MM-DDTHH:MM
    const pad = (n) => String(n).padStart(2, "0");
    const value = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    this.dateInputTarget.value = value;
  }

  flash(el, msg) {
    el.focus();
    el.setCustomValidity(msg);
    el.reportValidity();
    el.addEventListener("input", () => el.setCustomValidity(""), { once: true });
  }

  render() {
    // Show only the active step
    this.stepTargets.forEach((step) => {
      const num = parseInt(step.dataset.step, 10);
      step.hidden = num !== this.stepValue;
    });

    // Update header label
    if (this.hasStepLabelTarget) {
      this.stepLabelTarget.textContent = `Step ${this.stepValue} of 3`;
    }

    // Step 3 copy adapts to mode
    if (this.hasStep3TitleTarget) {
      this.step3TitleTarget.textContent =
        this.modeValue === "upcoming" ? "What's the agenda?" : "What happened?";
    }
    if (this.hasStep3HelpTarget) {
      this.step3HelpTarget.textContent =
        this.modeValue === "upcoming"
          ? "Topics, agenda, or context for the upcoming conversation."
          : "Key takeaways, decisions, or action items from the conversation.";
    }
    if (this.hasNotesInputTarget) {
      this.notesInputTarget.placeholder =
        this.modeValue === "upcoming"
          ? "Topics or agenda…"
          : "Notes from the conversation…";
    }

    // Summary lines on step 2 / 3
    if (this.hasModeSummaryTarget) {
      this.modeSummaryTarget.textContent =
        this.modeValue === "upcoming" ? "Coming up" : "Past";
    }
    if (this.hasTypeSummaryTarget && this.hasTypeInputTarget) {
      this.typeSummaryTarget.textContent = this.typeInputTarget.value || "—";
    }
    if (this.hasDateSummaryTarget && this.hasDateInputTarget && this.dateInputTarget.value) {
      const d = new Date(this.dateInputTarget.value);
      if (!isNaN(d)) {
        this.dateSummaryTarget.textContent = d.toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      }
    }

    // Nav buttons
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.hidden = this.stepValue === 1;
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.hidden = this.stepValue !== 2;
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.hidden = this.stepValue !== 3;
      this.submitButtonTarget.textContent =
        this.modeValue === "upcoming" ? "Add to timeline" : "Save";
    }
  }
}
